import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SocketKey } from '@/Config/api/socketKey';
import { useSocket } from '@/Domain/components/SocketProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { UploadItemTypeEnum } from '@/Domain/enum/UploadItemType.enum';
import { UploadStatusEnum } from '@/Domain/enum/UploadStatus.enum';
import { UploadFileHelper } from '@/Domain/helper/UploadFile.helper';
import { UploadUrlModalHelper } from '@/Domain/helper/UploadUrlModal.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { UploadFile } from '@/Domain/store/upload.store';

type Props = {
  successMessage: string | null;
  hasPendingFiles: boolean;
  hasErrorFiles: boolean;
  getFileSizeText: (fileSize: number) => string;
  getFileIcon: (fileName: string, fileType?: UploadItemTypeEnum) => string;
  getUrlIcon: (url: string) => string;
  getUrlDisplayName: (url: string) => string;
};

export const useArticleFileStatus = (files: UploadFile[]): Props => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { socket } = useSocket();
  const { capture } = usePostHogAnalytics();
  const { t } = useTranslation('knowledge_base');

  const hasPendingFiles = files.some((file) =>
    [
      UploadStatusEnum.PENDING,
      UploadStatusEnum.PARSING,
      UploadStatusEnum.PARSING_FALLBACK,
    ].includes(file.status),
  );

  useEffect(() => {
    if (hasPendingFiles && successMessage) {
      setSuccessMessage(null);
    }
  }, [hasPendingFiles, successMessage]);

  useEffect(() => {
    const handleKnowledgeBaseUpdated = () => {
      if (!hasPendingFiles) {
        setSuccessMessage(t('modals.status_modal.status_message.adding_knowledge_successful'));
      }
    };

    socket.on(SocketKey.knowledgeBase.knowledgeBaseUpdated, handleKnowledgeBaseUpdated);

    return () => {
      socket.off(SocketKey.knowledgeBase.knowledgeBaseUpdated, handleKnowledgeBaseUpdated);
    };
  }, [socket, hasPendingFiles, t]);

  const hasErrorFiles = files.some((file) => file.status === UploadStatusEnum.ERROR);

  // Close the upload funnel — ArticleUploadStarted fires on submit (in
  // useImportUrl/useUploadArticles); we match it with a terminal Completed or
  // Failed event so funnel conversion + time-to-complete are measurable. Only
  // emit once per transition to non-pending: the hook runs on every re-render.
  const wasPendingRef = useRef(hasPendingFiles);
  useEffect(() => {
    if (wasPendingRef.current && !hasPendingFiles && files.length > 0) {
      const urlFileCount = files.filter((f) => f.type === UploadItemTypeEnum.URL).length;
      const fileFileCount = files.length - urlFileCount;
      const totalScrapedPages = files.reduce(
        (sum, f) => sum + (f.scrapingProgress?.visitedItems ?? 0),
        0,
      );
      const props = {
        total_files: files.length,
        url_files: urlFileCount,
        file_files: fileFileCount,
        scraped_pages: totalScrapedPages,
      };
      capture(
        hasErrorFiles
          ? PostHogEventEnum.ArticleUploadFailed
          : PostHogEventEnum.ArticleUploadCompleted,
        props,
      );
    }
    wasPendingRef.current = hasPendingFiles;
  }, [hasPendingFiles, hasErrorFiles, files, capture]);

  return {
    successMessage,
    hasPendingFiles,
    hasErrorFiles,
    getFileSizeText: UploadFileHelper.getFileSizeText,
    getFileIcon: UploadFileHelper.getFileIcon,
    getUrlIcon: UploadUrlModalHelper.getUrlIcon,
    getUrlDisplayName: UploadUrlModalHelper.getUrlDisplayName,
  };
};
